import React from 'react';
import { Linkedin, Twitter, Instagram, Youtube, Mail } from 'lucide-react';
import { useLang } from '../contexts/LanguageContext';
import { SOCIAL } from '../mock/mock';

const LOGO_URL = '/desertnimbus-logo.png';

const iconFor = (key) => {
  switch (key) {
    case 'linkedin': return Linkedin;
    case 'x': return Twitter;
    case 'instagram': return Instagram;
    case 'youtube': return Youtube;
    default: return Linkedin;
  }
};

const Footer = () => {
  const { t } = useLang();
  return (
    <footer className="relative border-t border-cream/10 pt-16 pb-10 bg-[#0a1220]">
      <div className="max-w-[1600px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-5">
            <img
              src={LOGO_URL}
              alt="DesertNimbus Technologies"
              className="h-20 w-auto object-contain mb-6"
              style={{ filter: 'drop-shadow(0 4px 12px rgba(201, 154, 61, 0.15))' }}
            />
            <p className="text-cream/55 text-[13px] leading-[1.75] max-w-[400px]">{t.footer.body}</p>

            {/* Social */}
            <div className="mt-8">
              <div className="font-mono text-[10px] tracking-[0.35em] text-[#c99a3d] mb-4">{t.footer.follow}</div>
              <div className="flex items-center gap-3">
                {SOCIAL.map((s) => {
                  const Icon = iconFor(s.key);
                  return (
                    <a
                      key={s.key}
                      href={s.href}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={s.name}
                      className="w-10 h-10 rounded-full border border-cream/15 flex items-center justify-center text-cream/70 hover:text-[#c99a3d] hover:border-[#c99a3d]/60 hover:bg-cream/5 transition-all"
                    >
                      <Icon className="w-4 h-4" strokeWidth={1.5} />
                    </a>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="md:col-span-2">
            <div className="font-mono text-[10px] tracking-[0.35em] text-[#c99a3d] mb-5">{t.footer.explore}</div>
            <ul className="space-y-3 text-cream/65 text-[13px]">
              <li><a href="#technology" className="hover:text-cream transition-colors">{t.nav.technology}</a></li>
              <li><a href="#solutions" className="hover:text-cream transition-colors">{t.nav.solutions}</a></li>
              <li><a href="#about" className="hover:text-cream transition-colors">{t.nav.about}</a></li>
              <li><a href="#contact" className="hover:text-cream transition-colors">{t.nav.contact}</a></li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <div className="font-mono text-[10px] tracking-[0.35em] text-[#c99a3d] mb-5">{t.footer.missions}</div>
            <ul className="space-y-3 text-cream/65 text-[13px]">
              {t.footer.missionsList.map((m) => (<li key={m}>{m}</li>))}
            </ul>
          </div>

          <div className="md:col-span-3">
            <div className="font-mono text-[10px] tracking-[0.35em] text-[#c99a3d] mb-5">{t.footer.station}</div>
            <div className="space-y-3 text-cream/65 text-[13px]">
              <div>{t.contact.locationV}</div>
              <div>{t.contact.coordsV}</div>
              <a href="mailto:info@desertnimbus.com" className="flex items-center gap-2 text-cream hover:text-[#c99a3d] transition-colors">
                <Mail className="w-3.5 h-3.5" strokeWidth={1.5} /> info@desertnimbus.com
              </a>
            </div>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-cream/10 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="font-mono text-[10px] tracking-[0.3em] text-cream/45 order-2 md:order-1">{t.footer.copyright}</div>

          {/* UAE national badges */}
          <div className="order-1 md:order-2 flex items-center gap-8 md:gap-10">
            <a
              href="https://uaelogos.ae/en/logos/proud-of-uae-1"
              target="_blank"
              rel="noreferrer"
              aria-label="Proud of UAE"
              className="flex items-center group"
            >
              <img
                src="/proud-of-uae.png"
                alt="Proud of UAE"
                className="h-20 md:h-24 w-auto object-contain transition-transform duration-500 group-hover:scale-105"
                style={{ filter: 'drop-shadow(0 4px 14px rgba(201, 154, 61, 0.2))' }}
              />
            </a>
            <div className="w-px h-14 bg-cream/15"></div>
            <div className="flex items-center group" aria-label="Pledge & Commitment">
              <img
                src="/pledge-commitment.png"
                alt="Pledge & Commitment"
                className="h-16 md:h-20 w-auto object-contain transition-transform duration-500 group-hover:scale-105"
                style={{ filter: 'drop-shadow(0 4px 14px rgba(201, 154, 61, 0.15))' }}
              />
            </div>
          </div>

          <div className="flex gap-6 font-mono text-[10px] tracking-[0.25em] text-cream/45 order-3">
            <a href="#" className="hover:text-cream transition-colors">{t.footer.privacy}</a>
            <a href="#" className="hover:text-cream transition-colors">{t.footer.terms}</a>
            <a href="#" className="hover:text-cream transition-colors">{t.footer.legal}</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
